import type { Identifier } from "ra-core";

import { getLocalDateParts } from "../incentives/periods";
import type { InventoryItem } from "../types";

/**
 * Almikia Epic EC2d (TASK-139): pure helpers behind the Inventario statistics. They never rank or
 * compare advisors: days in inventory is an aggregate of the available stock, and a captured count
 * is one advisor's own figure for one month.
 */

const MS_PER_DAY = 86_400_000;

/** The `status` of an option or unit that is still on the market. */
export const AVAILABLE_STATUS = "disponible";

/** Whole days from a listing date (YYYY-MM-DD) to the local (Mexico City) date of `now`, never negative. */
export const daysInInventory = (addedOn: string, now: Date): number => {
  const [year, month, day] = addedOn.split("-").map(Number);
  const today = getLocalDateParts(now);
  const diff =
    Date.UTC(today.year, today.month - 1, today.day) -
    Date.UTC(year, month - 1, day);
  return Math.max(0, Math.round(diff / MS_PER_DAY));
};

/**
 * Average days in inventory of the AVAILABLE stock only, rounded to a whole day; null when no item
 * is available (so the card shows no figure instead of a misleading 0).
 */
export const averageDaysInInventory = (
  items: Pick<InventoryItem, "added_on" | "status">[],
  now: Date,
): number | null => {
  const available = items.filter((item) => item.status === AVAILABLE_STATUS);
  if (available.length === 0) return null;
  const total = available.reduce(
    (sum, item) => sum + daysInInventory(item.added_on, now),
    0,
  );
  return Math.round(total / available.length);
};

/** First day of the month after `monthStart` (both `YYYY-MM-01`). */
export const nextMonthStart = (monthStart: string): string => {
  const [year, month] = monthStart.split("-").map(Number);
  return month === 12
    ? `${year + 1}-01-01`
    : `${year}-${String(month + 1).padStart(2, "0")}-01`;
};

/**
 * Properties one advisor captured in a month: options whose responsable is the advisor and whose
 * listing date falls in the month of `monthStart` (`YYYY-MM-01`). Options owned by the agency
 * (AMK) count for no advisor.
 */
export const capturedInMonth = (
  options: Pick<
    InventoryItem,
    "responsable_sales_id" | "responsable_is_amk" | "added_on"
  >[],
  advisorId: Identifier,
  monthStart: string,
): number => {
  const month = monthStart.slice(0, 7);
  return options.filter(
    (option) =>
      !option.responsable_is_amk &&
      option.responsable_sales_id != null &&
      String(option.responsable_sales_id) === String(advisorId) &&
      option.added_on.slice(0, 7) === month,
  ).length;
};
